import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Calendar, ExternalLink, Clock, Video } from "lucide-react";

export default function BookCall() {
  const [settings, setSettings] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const bookingUrl = settings?.booking_url;
  const isPlaceholder = !bookingUrl || bookingUrl.includes("placeholder");

  useEffect(() => {
    if (isPlaceholder) return;

    if (window.Bookings) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://bookings.nimbuspop.com/assets/embed.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [isPlaceholder]);

  useEffect(() => {
    if (!scriptLoaded || !bookingUrl || isPlaceholder) return;

    if (window.Bookings && window.Bookings.inlineEmbed) {
      try {
        const container = document.getElementById("inline-container");
        if (container) {
          container.innerHTML = "";
        }
        window.Bookings.inlineEmbed({
          url: bookingUrl,
          parent: "#inline-container",
          height: "600px"
        });
      } catch (e) {
        console.error("Failed to initialize Zoho Bookings SDK embed:", e);
      }
    }
  }, [scriptLoaded, bookingUrl, isPlaceholder]);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10" data-testid="book-page">
      <Seo title="Book a Call" description="Schedule a 30-minute intro call via Zoho Calendar." path="/book" />
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Book a Call</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 leading-[1.05]">
            {settings?.booking_heading || "Let's build something together"}
          </h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            {settings?.booking_subtext || "Pick a slot that works for you."}
          </p>
        </div>
      </Reveal>

      <div className="grid sm:grid-cols-3 gap-4 mt-12">
        {[
          { icon: Clock, title: "30 minutes", text: "A focused intro call" },
          { icon: Video, title: "Video or audio", text: "Whatever works for you" },
          { icon: Calendar, title: "Zoho Bookings", text: "Synced to my availability" },
        ].map((c, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="glass rounded-2xl p-6 text-center">
              <c.icon className="w-5 h-5 mx-auto" />
              <div className="font-heading text-lg mt-3">{c.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{c.text}</div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-12 glass rounded-3xl p-3 overflow-hidden">
          {isPlaceholder ? (
            <div className="rounded-2xl bg-secondary/50 p-12 text-center" data-testid="booking-placeholder">
              <Calendar className="w-10 h-10 mx-auto text-muted-foreground" />
              <h3 className="font-heading text-xl mt-4">Booking link coming soon</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                The Zoho Bookings link can be configured in the admin panel. Once set, your booking embed appears right here.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 rounded-2xl p-4 text-xs sm:text-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-base">⚠️</span>
                  <span>
                    <strong>Browser Notice:</strong> If the booking system fails to send OTP or load slots, it is due to browser cookie restrictions on embedded frames. Opening the page directly resolves this.
                  </span>
                </div>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-xl transition-colors shrink-0 text-center text-xs whitespace-nowrap"
                >
                  Open Booking Page
                </a>
              </div>
              <div
                id="inline-container"
                data-testid="booking-iframe"
                className="w-full min-h-[600px] rounded-2xl overflow-hidden bg-white/5 dark:bg-black/5 border-0"
              />
            </div>
          )}
        </div>
      </Reveal>

      {!isPlaceholder && (
        <div className="text-center mt-6">
          <a href={bookingUrl} target="_blank" rel="noreferrer" data-testid="booking-open-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            Open booking in new tab <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}
